<script setup lang="ts">
import { Check, Info, Share } from 'lucide-vue-next';
import type { DeepLink, ErrorDto } from '@optionway/models';

const { loading } = useSearchResultsQueryState();

const route = useRoute();
const { origin } = useRequestURL();
const { $appConfig } = useNuxtApp();
const apiUrl = $appConfig?.api?.url;

const searchResultIndexStore = useSearchResultIndexStore();
const sortOptionsStore = useSortOptionsStore();
const deepLinkStore = useDeepLinkStore();
const gtmStore = useGtmStore();
const sessionData = useSessionData();

const sortInfoOpen = ref(false);

const { copy, copied } = useClipboard({ legacy: true });

const { mutate: createLink } = useMutation<DeepLink, string, ErrorDto>({
  mutation: (queryString) =>
    $fetch<DeepLink>(`${apiUrl}/search/link`, {
      method: 'POST',
      body: {
        queryString,
        path: route.path,
        sessionData: sessionData.value,
      },
    }),
  onSuccess: (deepLink, queryString) => {
    deepLinkStore.saveDeepLink(queryString, deepLink);
    void copy(deepLinkStore.shareableUrl(origin, route.path, queryString));
  },
});

const share = () => {
  if (deepLinkStore.hasShortLink) void copy(deepLinkStore.shareableUrl(origin, route.path));
  else createLink(deepLinkStore.fullQueryString);
  gtmStore.trackShare();
};
</script>

<template>
  <div class="flex items-center gap-5 py-4">
    <template v-if="loading">
      <UiSkeleton
        class="h-6 w-48"
        role="status"
        :aria-label="$t('components.results.list.loading')"
      />
    </template>
    <template v-else>
      <div class="flex items-center gap-2">
        <span
          aria-live="polite"
          aria-atomic="true"
          class="relative text-base font-semibold text-text-primary-900 md:text-xl"
        >
          <Transition
            enter-active-class="transition-opacity duration-200"
            leave-active-class="transition-opacity duration-200 absolute"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
          >
            <span
              :key="searchResultIndexStore.availableSearchResults.length"
              class="tabular-nums"
              >{{ searchResultIndexStore.availableSearchResults.length }}</span
            >
          </Transition>
          {{ $t('components.results.list.resultsFound', searchResultIndexStore.availableSearchResults.length) }}
        </span>
        <span class="hidden items-center gap-1 text-sm font-normal text-text-secondary-600 md:inline-flex">
          {{ $t('components.results.list.sortedBy') }}
          {{ $t(`components.results.sortOptions.shortLabel.${sortOptionsStore.sortBy}`) }}
          <button
            type="button"
            class="inline-flex items-center text-text-secondary-600"
            :aria-label="$t('components.results.sortOptions.sortInfo.title')"
            @click="sortInfoOpen = true"
          >
            <Info
              class="h-4 w-4"
              aria-hidden="true"
            />
          </button>
        </span>
      </div>
    </template>

    <UiCustomSeparatorLine
      aria-hidden="true"
      width-class="flex-1 self-center"
      color-class="bg-gray-300"
    />

    <UiButton
      variant="secondary"
      color="gray"
      size="sm"
      class="h-8 w-8 gap-1 p-0 md:h-9 md:w-auto md:px-3 [&_svg]:size-4"
      :aria-label="copied ? $t('components.results.filters.shareCopied') : $t('components.results.filters.share')"
      @click="share"
    >
      <Check v-if="copied" />
      <Share v-else />
      <span class="hidden md:inline">
        {{ copied ? $t('components.results.filters.shareCopied') : $t('components.results.filters.share') }}
      </span>
    </UiButton>
  </div>
  <ResultsSortInfoDialog v-model:open="sortInfoOpen" />
</template>
